import { useLanguage } from '@/app/providers';
import { CloseIcon, PreviewForward, PreviewStarActive, TrashIcon, UnStarIcon } from '@/assets/images';
import { resetAlertModal, setAlertModal } from '@/components/AlertModalRoot/alertModalService';
import { showPopup } from '@/components/PopupContainer/popupService';
import { blockOfflineAction, getUserIdFromJid, logger } from '@/shared/utils';
import { showErrorToast } from '@/shared/utils/toastMessage';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useChatFeature } from '../chatsRegistry';
import {
   getSelectedMessages,
   isAnyMessageWithinDeleteDuration,
   resetSelectedMessages,
   useAllSelectedStarred,
   useMessageSelectionCount,
} from '../model';
import { getAmIParticipant, useAmIParticipant } from '../model/groupParticipants';
import ForwardMessagePopup from '../sub-features/forward-message/ui/ForwardMessage';
import { handleDeleteMessages } from '../utils/messageAction';
import { handleSendMessage } from '../utils/sendUtils';
import { toggleStarMessage } from '../utils/starredMessageUtils';

/**
 * ViewModel for ConversationFooter.
 * Owns: message send, selection mode actions (copy/delete/star/forward/info), feature gates.
 * ConversationFooter.jsx renders only.
 */
export const useConversationFooter = activeChatJid => {
   const activeChatId = getUserIdFromJid(activeChatJid);
   const dispatch = useDispatch();
   const { t } = useLanguage();

   const messageActionFeatures = useChatFeature('message-action') || {};
   const isForwardEnabled = messageActionFeatures['message-action-forward'];
   const isStarEnabled =
      useSelector(state => state.features.isStarMessageEnabled ?? true) && messageActionFeatures['message-action-star'];
   const isDeleteEnabled =
      useSelector(state => state.features.isDeleteMessageEnabled ?? true) &&
      messageActionFeatures['message-action-delete'];

   const allStarred = useAllSelectedStarred(activeChatId);
   const { selectionCount, selectionType } = useMessageSelectionCount(activeChatId);
   const amIParticipant = useAmIParticipant(activeChatId);

   const handleSend = useCallback(() => {
      handleSendMessage({ activeChatJid });
   }, [activeChatJid]);

   const handleResetSelection = useCallback(() => {
      dispatch(resetSelectedMessages({ chatId: activeChatId }));
   }, [dispatch, activeChatId]);

   // Action handlers for selection mode
   const handleForward = useCallback(() => {
      if (!isForwardEnabled) return;
      showPopup({
         id: 'forward_message_popup',
         title: 'Forward Message',
         body: <ForwardMessagePopup activeChatJid={activeChatJid} />,
         className: 'forward-message-popup-modal',
         visible: true,
      });
   }, [isForwardEnabled, activeChatJid]);

   const handleDelete = useCallback(() => {
      if (!isDeleteEnabled) return;

      const rawSelected = getSelectedMessages(activeChatId);
      if (!rawSelected.length) return;

      const selectedMessages = rawSelected.flatMap(msg =>
         msg.type === 'groupedMedia' && Array.isArray(msg.messages) ? msg.messages : [msg],
      );

      const multiple = selectedMessages.length > 1;
      const msgIds = selectedMessages.map(m => m.msgId || m.messageId);
      const isGroupChat = activeChatJid.includes('@mix');

      const deleteForEveryOne = isAnyMessageWithinDeleteDuration(selectedMessages) && (!isGroupChat || amIParticipant);

      setAlertModal({
         id: 'delete_message',
         visible: true,
         title: t('message.DELETE_TITLE'),
         description: multiple ? t('message.DELETE_DESC_PLURAL') : t('message.DELETE_DESC_SINGLE'),
         icon: (
            <div className="alert-modal-icon-inner ct-flexCenter">
               <TrashIcon />
            </div>
         ),

         yesButton: deleteForEveryOne ? t('message.DELETE_FOR_EVERYONE') : t('message.DELETE_FOR_ME'),

         yesAction: () => {
            if (blockOfflineAction()) {
               return;
            }

            if (deleteForEveryOne && isGroupChat && !getAmIParticipant(activeChatId)) {
               showErrorToast(t('toast.DELETE_FOR_EVERYONE_NOT_PARTICIPANT'));
               return;
            }

            handleDeleteMessages({
               jid: activeChatJid,
               msgIds,
               deleteForEveryOne,
            });

            dispatch(resetSelectedMessages({ chatId: activeChatId }));

            resetAlertModal('delete_message');
         },

         ...(deleteForEveryOne
            ? {
                 closeIcon: <CloseIcon />,
              }
            : {
                 noButton: t('common.CANCEL'),
                 noAction: () => logger.log('Cancelled'),
              }),

         optionalButton: deleteForEveryOne ? t('message.DELETE_FOR_ME') : undefined,

         optionalAction: deleteForEveryOne
            ? () => {
                 if (blockOfflineAction()) {
                    return;
                 }

                 handleDeleteMessages({
                    jid: activeChatJid,
                    msgIds,
                    deleteForEveryOne: false,
                 });

                 dispatch(resetSelectedMessages({ chatId: activeChatId }));

                 resetAlertModal('delete_message');
              }
            : undefined,

         onRequestClose: () => resetAlertModal('delete_message'),
         customClassName: 'alert-modal-delete-v2 delete-message-bubble-modal',
      });
   }, [isDeleteEnabled, activeChatId, activeChatJid, amIParticipant, dispatch, t]);

   const handleStar = useCallback(() => {
      if (!isStarEnabled) return;
      const rawSelected = getSelectedMessages(activeChatId);
      if (!rawSelected.length) return;

      // Unwrap groupedMedia — toggleStarMessage needs individual messages
      const msgs = rawSelected.flatMap(msg =>
         msg.type === 'groupedMedia' && Array.isArray(msg.messages) ? msg.messages : [msg],
      );

      const toProcess = allStarred ? msgs : msgs.filter(m => m.favouriteStatus !== 1);
      if (toProcess.length) toggleStarMessage(toProcess, activeChatJid);
      dispatch(resetSelectedMessages({ chatId: activeChatId }));
   }, [isStarEnabled, allStarred, activeChatId, activeChatJid, dispatch]);

   const actions = {
      forward: { id: 'forward', icon: <PreviewForward />, handler: handleForward, show: isForwardEnabled },
      star: {
         id: 'star',
         icon: allStarred ? <UnStarIcon /> : <PreviewStarActive />,
         handler: handleStar,
         show: isStarEnabled,
      },
      delete: { id: 'delete', icon: <TrashIcon />, handler: handleDelete, show: isDeleteEnabled },
   };

   const messageActions = selectionType ? { [selectionType]: actions[selectionType] } : {};

   return {
      selectionCount,
      selectionType,
      messageActions,
      amIParticipant,
      handleSend,
      handleResetSelection,
   };
};
